<template>
    <div class="marketing-preferences-attributes-table">
        <div class="marketing-preferences-attributes-table__header">
            <MarketingPreferencesAttributesSettingsHeader
                :filters="filters"
                :searchable="true"
                :searchValue="searchValue"
                @removeActiveFilters="filterAttributes"
                @updateFilter="filterAttributes"
                @search="searchAttributes"
            />
            <CompassCheckbox
                v-if="!isEmptyList && !isLoading"
                :modelValue="$t.__('common.actions.select_all')"
                :checked="isAllItemsChecked"
                :indeterminate="isIndeterminateCheckAllItems"
                class="marketing-preferences-attributes-table__table-header-select-all"
                @change="checkAllItems"
            />
        </div>
        <TableLoading v-if="isLoading" />
        <CompassTable v-else class="marketing-preferences-attributes-table__table" size="medium">
            <CompassTableBody>
                <template #empty v-if="isEmptyList">
                    <CompassEmptyState
                        illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
                        :title="
                            $t.__(
                                'marketing.preferences.attributes.MarketingPreferencesAttributesTable.no_result',
                            )
                        "
                    >
                        <CompassButton
                            :label="$t.__('common.actions.reset')"
                            @click="resetFiltersAndSearch"
                        />
                    </CompassEmptyState>
                </template>

                <CompassTableRow
                    v-for="row in displayableAttributes"
                    :key="`table-row--${row.id ? row.id : row.code}`"
                    :subhead="isSubhead(row)"
                >
                    <template #subhead v-if="isSubhead(row)">
                        <span> {{ row.label }} </span>
                    </template>
                    <template v-else>
                        <CompassTableBodyCell>
                            <div class="marketing-preferences-attributes-table__table-container">
                                <CompassTooltip
                                    :content="
                                        $t.__(
                                            'marketing.preferences.attributes.MarketingPreferencesAttributesTab.default_synchronized_field',
                                        )
                                    "
                                    :disabled="!row.default"
                                    placement="bottom-start"
                                >
                                    <CompassCheckbox
                                        :modelValue="row.label"
                                        :showLabel="false"
                                        :checked="row.synchronized || row.default"
                                        :disabled="row.default"
                                        @change="
                                            (value) => {
                                                row.synchronized = value;
                                            }
                                        "
                                    />
                                </CompassTooltip>

                                <span class="typographies-paragraph-medium-regular">
                                    {{ row.label }}
                                </span>
                            </div>
                        </CompassTableBodyCell>
                    </template>
                </CompassTableRow>
            </CompassTableBody>
        </CompassTable>
    </div>
</template>

<script>
import { debounce } from 'lodash';
import MarketingPreferencesAttributesSettingsHeader from 'vcomponents/marketing/preferences/attributes/settings/MarketingPreferencesAttributesSettingsHeader.vue';
import ListingFiltersSelect from 'vcomponents/common/listing/filters/types/ListingFiltersSelect.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';

const isSynchronizedId = 'yes';
const isNotSynchronizedId = 'no';

export default {
    components: {
        MarketingPreferencesAttributesSettingsHeader,
        TableLoading,
    },
    props: {
        categories: {
            type: Array,
            required: true,
        },
        attributes: {
            type: Array,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            allItems: this.$t.__('common.actions.select_all'),
            filters: [
                {
                    optionsSelected: [],
                    value: [],
                    label: this.$t.__(
                        'marketing.preferences.attributes.MarketingPreferencesAttributesTable.filter_type_label',
                    ),
                    activeLabel: this.$t.__(
                        'marketing.preferences.attributes.MarketingPreferencesAttributesTable.filter_type_active_label',
                    ),
                    code: 'fieldtype',
                    multiple: true,
                    applyLabel: this.$t.__('common.actions.apply'),
                    isActive: false,
                    options: this.categories,
                    displayDropdown: true,
                    component: ListingFiltersSelect,
                },
                {
                    optionsSelected: [],
                    value: [],
                    label: this.$t.__(
                        'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronized_fields',
                    ),
                    activeLabel: this.$t.__(
                        'marketing.preferences.attributes.MarketingPreferencesAttributes.synchronized_fields',
                    ),
                    code: 'fieldsync',
                    multiple: false,
                    isActive: false,
                    options: [
                        { label: this.$t.__('common.labels.yes'), id: isSynchronizedId },
                        { label: this.$t.__('common.labels.no'), id: isNotSynchronizedId },
                    ],
                    displayDropdown: true,
                    component: ListingFiltersSelect,
                },
            ],
            searchValue: '',
        };
    },
    computed: {
        displayableAttributes() {
            return this.attributes.filter((attribute) => !attribute.hide);
        },
        itemsCheckable() {
            return this.attributes.filter(
                (attributeDisplayed) =>
                    attributeDisplayed.synchronized !== undefined &&
                    (!attributeDisplayed.subheading || attributeDisplayed.subheading.length <= 0),
            );
        },
        isAllItemsChecked() {
            return this.itemsCheckable.every(
                (itemCheckable) => itemCheckable.synchronized || itemCheckable.default,
            );
        },
        isIndeterminateCheckAllItems() {
            return this.itemsCheckable.some(
                (itemCheckable) => itemCheckable.synchronized || itemCheckable.default,
            )
                ? !this.isAllItemsChecked
                : false;
        },
        isEmptyList() {
            return this.displayableAttributes.length === 0;
        },
    },
    methods: {
        checkAllItems(checkAllItems) {
            for (const attribute of this.attributes) {
                if (
                    (!attribute.subheading || attribute.subheading.length <= 0) &&
                    attribute.synchronized !== undefined &&
                    !attribute.default &&
                    !attribute.hide
                ) {
                    attribute.synchronized = checkAllItems;
                }
            }
        },
        resetFiltersAndSearch() {
            for (const filter of this.filters) {
                filter.value = [];
                filter.optionsSelected = [];
                filter.isActive = false;
            }
            this.searchValue = '';
            this.filterAttributes();
        },
        filterAttributes(forceSkeletonLoading = true) {
            if (forceSkeletonLoading) {
                this.$emit('update:isLoading', true);
            }
            for (const attribute of this.attributes) {
                if (!attribute.subheading || attribute.subheading.length <= 0) {
                    attribute.hide = false;

                    for (const filter of this.filters) {
                        if (filter.isActive) {
                            switch (filter.code) {
                                case 'fieldtype':
                                    if (
                                        filter.value.length > 0 &&
                                        !filter.value.includes(attribute.category.code)
                                    ) {
                                        attribute.hide = true;
                                    }
                                    break;
                                case 'fieldsync':
                                    if (
                                        filter.value.length === 1 &&
                                        ((filter.value.includes(isSynchronizedId) &&
                                            !attribute.synchronized) ||
                                            (filter.value.includes(isNotSynchronizedId) &&
                                                (attribute.synchronized || attribute.default)))
                                    ) {
                                        attribute.hide = true;
                                    }
                                    break;
                            }
                        }

                        if (
                            this.searchValue !== '' &&
                            !attribute.label.toLowerCase().includes(this.searchValue.toLowerCase())
                        ) {
                            attribute.hide = true;
                        }
                    }
                }
            }

            for (const attribute of this.attributes) {
                if (attribute.subheading && attribute.subheading.length > 0) {
                    this.attributes.filter(
                        (att) =>
                            (!att.subheading || att.subheading.length <= 0) &&
                            att.category.code === attribute.code &&
                            att.hide === false,
                    ).length === 0
                        ? (attribute.hide = true)
                        : (attribute.hide = false);
                }
            }

            this.$forceUpdate();
            if (forceSkeletonLoading) {
                setTimeout(() => {
                    this.$emit('update:isLoading', false);
                }, 200);
            }
        },
        searchAttributes(value) {
            this.searchValue = value;
            debounce(() => {
                this.filterAttributes(false);
            }, 500)();
        },
        isSubhead(row) {
            return row.subheading && row.subheading.length > 0;
        },
    },
};
</script>
<style lang="scss" scoped>
.marketing-preferences-attributes-table {
    &__header {
        border-bottom: 1px solid var(--colors-common-border-lighter);
        border-radius: 4px 4px 0 0;
        background-color: var(--colors-common-background-default);
    }
    &__table {
        border-top: 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        overflow-x: hidden;

        &-header-select-all {
            padding: var(--spacing-3);
        }

        .marketing-preferences-attributes__table-empty {
            padding-top: var(--spacing-5);
            padding-bottom: var(--spacing-5);
            &:hover {
                background-color: var(--colors-common-background-default);
            }
        }

        .marketing-preferences-attributes-table__table-container {
            display: flex;
            align-items: center;
            gap: var(--spacing-5);
        }
    }
}
</style>
